// src/App.tsx
import React from 'react';
import { useProcessing } from './context/ProcessingContext';
import { DialogProvider } from './context/DialogContext';
import { Dialog } from './components/Dialog/Dialog';

// Component imports - using default imports
import { TopPanel } from './components/Navigation/TopPanel';
import { Sidebar } from './components/Navigation/Sidebar';
import { InterpolationPanel } from './components/Interpolation/InterpolationPanel';
import { UploadComponent } from './components/Upload/UpLoadComponent';
import { Gridding } from './components/Grid/Gridding';
import { MapVisualization } from './components/Map/MapVisualization'; 
import { ContourVisualization } from './components/Contour/ContourVisualization';
import { ExportPanel } from './components/Export/ExportPanel';

const App: React.FC = () => {
  const { state, dispatch } = useProcessing();

  // Helper function to render the current step component
  const renderCurrentStep = () => {
    switch (state.currentViewStep) {
      case 'upload':
        return <UploadComponent />;

      case 'grid':
        if (!state.rawData) {
          return (
            <div className="panel-section">
              <h2>Grid Configuration</h2>
              <p>Please complete the upload step first.</p>
            </div>
          );
        }
        return <Gridding />;

      case 'interpolate':
        if (!state.griddedData) {
          return (
            <div className="panel-section">
              <h2>Interpolation</h2>
              <p>Please complete the grid step first.</p>
            </div>
          );
        }
        return <InterpolationPanel />;

      case 'contour':
        if (!state.interpolatedData) {
          return (
            <div className="panel-section">
              <h2>Contour Visualization</h2>
              <p>Please complete the interpolation step first.</p>
            </div>
          );
        }
        return <ContourVisualization />;
      case 'map':
        if (!state.interpolatedData) {
          return (
            <div className="panel-section">
              <h2>Interactive Map</h2>
              <p>Please complete the interpolation step first.</p>
            </div>
          );
        }
        // Check if we have geographic data
        if (!state.rawData?.geographic_metadata?.original_crs) {
          return (
            <div className="panel-section">
              <h2>Interactive Map</h2>
              <p>Interactive mapping is only available for georeferenced data.</p>
              <p>Your data does not contain geographic coordinates.</p>
            </div>
          );
        }
        return <MapVisualization />;

      case 'export':
        if (!state.interpolatedData) {
          return (
            <div className="panel-section">
              <h2>Export Results</h2>
              <p>Please complete the interpolation step first.</p>
            </div>
          );
        }
        return <ExportPanel />;

      default:
        return null;
    }
  };

  return (
    <DialogProvider>
      <>
        <TopPanel />
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            {/* Global Error Display */}
            {state.error && (
              <div className="error-message" role="alert">
                {state.error}
                <button 
                  className="error-dismiss"
                  onClick={() => dispatch({ type: 'SET_ERROR', payload: null })}
                  aria-label="Dismiss error"
                >
                  ×
                </button>
              </div>
            )}

            {/* Global Processing Indicator */}
            {state.isProcessing && (
              <div className="processing-banner" role="status">
                <div className="processing-spinner" />
                <span>Processing your data...</span>
              </div>
            )}

            {/* Main Content Area */}
            <div className="content-area">
              {renderCurrentStep()}
            </div>

            {/* Global Loading Overlay */}
            {state.isProcessing && (
              <div className="loading-overlay" role="alert" aria-busy="true">
                <div className="loading-spinner" />
                <p>Processing...</p>
              </div>
            )}
          </main>
        </div>
        <Dialog />
      </>
    </DialogProvider>
  );
};

export default App;